import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, 
  Globe, 
  Clock, 
  CheckCircle, 
  ExternalLink,
  Award,
  Loader2,
  AlertCircle,
  Play,
  RefreshCw
} from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { API_BASE } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import ContentFeedback from '../components/ContentFeedback';
import { appendAiccUserParams } from '../utils/aiccIdentity';

const API = `${API_BASE}/api`;

// Poll interval for checking AICC completion status (in ms)
const COMPLETION_POLL_INTERVAL = 5000; // 5 seconds

export default function ExternalCoursePlayer() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [launching, setLaunching] = useState(false);
  const [enrollment, setEnrollment] = useState(null);
  const [showIframe, setShowIframe] = useState(false);
  const [launchUrl, setLaunchUrl] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [completionStatus, setCompletionStatus] = useState(null);
  const [markingComplete, setMarkingComplete] = useState(false);
  const [showManualComplete, setShowManualComplete] = useState(false);
  
  const pollIntervalRef = useRef(null);

  // Use shared utility to append user params to AICC URL
  const appendUserParams = useCallback((url) => appendAiccUserParams(url, user), [user]);

  useEffect(() => {
    fetchCourse();
    return () => {
      // Cleanup polling on unmount
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [courseId, token]);

  // Poll for completion status when iframe is shown
  useEffect(() => {
    if (showIframe && sessionId && enrollment?.status !== 'completed') {
      // Start polling for completion
      pollIntervalRef.current = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/external-courses/${courseId}/session-status`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          setCompletionStatus(res.data);
          
          // If completed via AICC, update local state and show success
          if (res.data.completed && enrollment?.status !== 'completed') {
            setEnrollment(prev => ({ ...prev, status: 'completed', progress: 100 }));
            
            toast.success(
              <div className="flex flex-col gap-1">
                <span className="font-medium">Course Completed! 🎉</span>
                <span className="text-sm text-zinc-400">
                  {res.data.score ? `Score: ${res.data.score} • ` : ''}+2 XP earned
                </span>
              </div>
            );
            
            // Stop polling
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
            }
          }
        } catch (error) {
          console.error('Error polling completion status:', error);
        }
      }, COMPLETION_POLL_INTERVAL);
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [showIframe, sessionId, enrollment?.status, courseId, token]);

  const fetchCourse = async () => {
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const res = await axios.get(`${API}/external-courses/${courseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourse(res.data);
      setEnrollment(res.data.enrollment);
    } catch (error) {
      console.error('Error fetching course:', error);
      if (error.response?.status === 404) {
        toast.error('Course not found');
        navigate('/courses');
      } else if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStartCourse = async () => {
    setLaunching(true);
    try {
      // Launch with AICC tracking
      const res = await axios.post(`${API}/external-courses/${courseId}/launch`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Append user identification params for seamless login on AICC provider
      const urlWithUser = appendUserParams(res.data.launch_url);
      console.log('External Course: Launch URL with user params:', urlWithUser);
      
      setLaunchUrl(urlWithUser);
      setSessionId(res.data.session_id);
      setEnrollment(prev => prev || { status: 'active', progress: 0 });
      setShowIframe(true);
      setShowManualComplete(true); // Enable manual completion after starting
      
      // Show appropriate toast based on resume status
      if (res.data.is_resume) {
        toast.success('Welcome back! Your progress has been restored.');
      } else {
        toast.success(
          <div className="flex flex-col gap-1">
            <span>Course started!</span>
            <span className="text-sm text-zinc-400">Complete the quiz, then click &quot;Mark as Complete&quot; below</span>
          </div>
        );
      }
    } catch (error) {
      console.error('Error launching course:', error);
      toast.error('Failed to start course');
    } finally {
      setLaunching(false);
    }
  };

  const handleRefreshStatus = async () => {
    try {
      const res = await axios.get(`${API}/external-courses/${courseId}/session-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCompletionStatus(res.data);
      
      if (res.data.completed) {
        setEnrollment(prev => ({ ...prev, status: 'completed', progress: 100 }));
        toast.success('Course completion confirmed!');
      } else {
        toast.info(`Current status: ${res.data.lesson_status || 'In Progress'}`);
      }
    } catch (error) {
      toast.error('Failed to check status');
    }
  };

  const handleManualComplete = async () => {
    setMarkingComplete(true);
    try {
      const res = await axios.post(`${API}/external-courses/${courseId}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.success || res.data.already_completed) {
        setEnrollment(prev => ({ ...prev, status: 'completed', progress: 100 }));
        toast.success(
          <div className="flex flex-col gap-1">
            <span className="font-medium">Course Completed! 🎉</span>
            <span className="text-sm text-zinc-400">
              {res.data.xp_awarded ? `+${res.data.xp_awarded} XP earned` : 'Well done!'}
            </span>
          </div>
        );
        setShowManualComplete(false);
      }
    } catch (error) {
      console.error('Error marking complete:', error);
      toast.error('Failed to mark course as complete');
    } finally {
      setMarkingComplete(false);
    }
  };

  const handleOpenInNewTab = null; // removed: learners stay on this platform

  const getLevelColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'introductory': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'intermediate': return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      case 'advanced': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-navigate-gold" />
        </div>
      </Layout>
    );
  }

  if (!course) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
          <AlertCircle className="h-12 w-12 text-red-400 mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Course Not Found</h2>
          <p className="text-zinc-400 mb-4">The course you&apos;re looking for doesn&apos;t exist.</p>
          <Button onClick={() => navigate('/courses')} variant="outline">
            Back to Courses
          </Button>
        </div>
      </Layout>
    );
  }

  const isCompleted = enrollment?.status === 'completed';
  const isEnrolled = !!enrollment;

  return (
    <Layout breadcrumbItems={[{ label: 'Dashboard', href: '/dashboard', isRoot: true }, { label: 'External Courses', href: '/courses?tab=external' }, { label: course.title, isCurrent: true }]}>
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/courses')}
            className="text-zinc-400 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Courses
          </Button>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Course Player / Preview */}
            <div className="bg-zinc-900 border border-zinc-700 rounded-xl overflow-hidden">
              {/* Gold accent bar */}
              <div className="h-1 bg-gradient-to-r from-navigate-gold to-amber-600" />
              
              {showIframe && launchUrl ? (
                <div className="relative">
                  <iframe
                    src={launchUrl}
                    className="w-full aspect-video"
                    title={course.title}
                    allowFullScreen
                    allow="autoplay; fullscreen"
                  />
                  {/* AICC Status indicator */}
                  {!isCompleted && (
                    <div className="absolute bottom-3 left-3 bg-zinc-900/90 px-3 py-1.5 rounded-lg flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                      <span className="text-sm text-white">
                        Tracking active - complete the quiz to finish
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="aspect-video bg-gradient-to-br from-zinc-800 to-zinc-900 flex flex-col items-center justify-center p-8">
                  <div className="h-20 w-20 rounded-full bg-navigate-gold/10 flex items-center justify-center mb-6">
                    <Globe className="h-10 w-10 text-navigate-gold/70" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{course.title}</h3>
                  
                  {/* Feedback Component */}
                  <div className="mb-4">
                    <ContentFeedback
                      contentType="external_module"
                      contentId={courseId}
                      token={token}
                      size="md"
                      showStats={true}
                    />
                  </div>
                  
                  <p className="text-zinc-400 text-center mb-6 max-w-md">
                    This is an external course from {course.provider || 'our partner'}. 
                    Complete the content and pass the quiz to earn your certificate.
                  </p>
                  <Button
                    onClick={handleStartCourse}
                    disabled={launching}
                    className="bg-navigate-gold hover:bg-amber-500 text-black px-8"
                    data-testid="start-course-btn"
                  >
                    {launching ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Play className="h-4 w-4 mr-2" />
                    )}
                    {isEnrolled ? 'Continue Course' : 'Start Course'}
                  </Button>
                </div>
              )}
            </div>

            {/* Course Details */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4">About This Course</h2>
              <p className="text-zinc-400 leading-relaxed">
                {course.description}
              </p>
              
              {/* Completion Instructions */}
              <div className="mt-4 p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
                <h3 className="text-sm font-medium text-white mb-2">How to Complete</h3>
                <ol className="text-sm text-zinc-400 space-y-1 list-decimal list-inside">
                  <li>Click &quot;Start Course&quot; to begin</li>
                  <li>Watch all the content in the course module</li>
                  <li>Complete and pass the quiz at the end</li>
                  <li>Return here and click &quot;Mark as Complete&quot;</li>
                </ol>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Progress Card */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${
                  isCompleted ? 'bg-green-500/20' : 'bg-navigate-gold/20'
                }`}>
                  {isCompleted ? (
                    <CheckCircle className="h-6 w-6 text-green-400" />
                  ) : (
                    <Globe className="h-6 w-6 text-navigate-gold" />
                  )}
                </div>
                <div>
                  <p className="font-medium text-white">
                    {isCompleted ? 'Completed' : isEnrolled ? 'In Progress' : 'Not Started'}
                  </p>
                  <p className="text-sm text-zinc-500">
                    {isCompleted 
                      ? 'You have completed this course'
                      : isEnrolled 
                        ? 'Complete the quiz to finish'
                        : 'Enroll to start learning'}
                  </p>
                </div>
              </div>

              {/* Progress Bar */}
              {isEnrolled && (
                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-zinc-400">Progress</span>
                    <span className={isCompleted ? 'text-green-400' : 'text-navigate-gold'}>
                      {enrollment.progress || 0}%
                    </span>
                  </div>
                  <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                    <div 
                      className={`h-full transition-all duration-500 ${
                        isCompleted ? 'bg-green-500' : 'bg-navigate-gold'
                      }`}
                      style={{ width: `${enrollment.progress || 0}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Refresh Status Button (when course is in progress) */}
              {isEnrolled && !isCompleted && showIframe && (
                <Button
                  onClick={handleRefreshStatus}
                  variant="outline"
                  className="w-full border-zinc-700 hover:border-navigate-gold mb-3"
                  data-testid="refresh-status-btn"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Check Completion Status
                </Button>
              )}

              {/* Manual Mark as Complete Button */}
              {showManualComplete && !isCompleted && (
                <div className="space-y-3">
                  <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <p className="text-sm text-amber-400 mb-2">
                      <strong>Finished the quiz?</strong>
                    </p>
                    <p className="text-xs text-zinc-400 mb-3">
                      If you&apos;ve completed and passed the course quiz, click below to confirm your completion.
                    </p>
                    <Button
                      onClick={handleManualComplete}
                      disabled={markingComplete}
                      className="w-full bg-navigate-gold hover:bg-amber-500 text-black"
                      data-testid="mark-complete-btn"
                    >
                      {markingComplete ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <CheckCircle className="h-4 w-4 mr-2" />
                      )}
                      Mark as Complete
                    </Button>
                  </div>
                </div>
              )}

              {isCompleted && (
                <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <Award className="h-5 w-5 text-green-400" />
                  <span className="text-sm text-green-400">
                    +2 XP earned for completing this course
                  </span>
                </div>
              )}

              {/* Completion Status Details */}
              {completionStatus && !isCompleted && (
                <div className="mt-3 p-3 bg-zinc-800/50 rounded-lg">
                  <p className="text-xs text-zinc-500 mb-1">AICC Status</p>
                  <p className="text-sm text-white capitalize">
                    {completionStatus.lesson_status || 'Not started'}
                  </p>
                  {completionStatus.score && (
                    <p className="text-sm text-navigate-gold mt-1">
                      Score: {completionStatus.score}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Course Info */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-4">
              <h3 className="font-medium text-white">Course Information</h3>
              
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" className="bg-zinc-800/50 text-zinc-300 border-zinc-600">
                  <Globe className="h-3 w-3 mr-1" />
                  External Course
                </Badge>
                {course.level && (
                  <Badge className={getLevelColor(course.level)}>
                    {course.level}
                  </Badge>
                )}
              </div>

              <div className="space-y-3 text-sm">
                {course.specialisation && (
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Specialisation</span>
                    <span className="text-white">{course.specialisation}</span>
                  </div>
                )}
                {course.original_category && (
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Category</span>
                    <span className="text-white">{course.original_category}</span>
                  </div>
                )}
                {course.estimated_duration && (
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Duration</span>
                    <span className="text-white flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5" />
                      {course.estimated_duration}
                    </span>
                  </div>
                )}
                {course.provider && (
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Provider</span>
                    <span className="text-white">{course.provider}</span>
                  </div>
                )}
                {course.cpd_points > 0 && (
                  <div className="flex justify-between">
                    <span className="text-zinc-500">CPD Points</span>
                    <span className="text-navigate-gold">{course.cpd_points}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
